import React from 'react';
import type { OjtMode, OjtModeTimeSegment } from '../../../types';
import { AlertTriangle, Check, X } from 'lucide-react';

interface ModePickerProps {
  isOpen: boolean;
  modes: OjtMode[];
  currentModeId?: string;
  /** Mode time log from the active session — used to compute time spent per mode */
  modeTimeLog?: OjtModeTimeSegment[];
  onSelect: (modeId: string) => void;
  onClose: () => void;
}

const formatMinutes = (mins: number): string => {
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
};

/** Compute total minutes spent in a given mode across all closed segments and the open segment if active. */
const minutesForMode = (log: OjtModeTimeSegment[] | undefined, modeId: string, currentModeId?: string): number => {
  if (!log || log.length === 0) return 0;
  let total = 0;
  for (const seg of log) {
    if (seg.modeId !== modeId) continue;
    if (seg.endTime !== undefined) {
      total += seg.durationMinutes ?? Math.max(0, Math.round((seg.endTime - seg.startTime) / 60000));
    } else if (modeId === currentModeId) {
      total += Math.max(0, Math.round((Date.now() - seg.startTime) / 60000));
    }
  }
  return total;
};

/**
 * Modal listing all available OJT modes with time spent in each.
 * Selecting a mode hands off to the parent — typically the parent triggers
 * mode-change sign-off (if configured) before persisting the switch.
 */
export const ModePicker: React.FC<ModePickerProps> = ({
  isOpen,
  modes,
  currentModeId,
  modeTimeLog,
  onSelect,
  onClose,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[85vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-aero-100">
          <div>
            <h3 className="text-base font-bold text-aero-900">Switch mode</h3>
            <p className="text-xs text-aero-500">Tap a mode to change how you and the lead hand are working together.</p>
          </div>
          <button onClick={onClose} className="p-1 -mr-1 text-aero-400 hover:text-aero-600 transition-colors" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-2">
          {modes.length === 0 && (
            <p className="text-sm text-aero-400 text-center py-6">No modes configured.</p>
          )}
          {modes.map((mode) => {
            const isCurrent = mode.id === currentModeId;
            const minutes = minutesForMode(modeTimeLog, mode.id, currentModeId);
            const requiredMins = mode.requiredMinutes;
            const meetsRequired = !requiredMins || minutes >= requiredMins;

            return (
              <button
                key={mode.id}
                type="button"
                onClick={() => onSelect(mode.id)}
                disabled={isCurrent}
                className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${
                  isCurrent
                    ? 'border-aero-300 bg-aero-50 opacity-90 cursor-default'
                    : 'border-aero-200 bg-white hover:border-aero-400 active:bg-aero-50'
                }`}
                style={{
                  backgroundColor: isCurrent ? `${mode.color}10` : undefined,
                  borderColor: isCurrent ? mode.color : undefined,
                }}
              >
                <span
                  className="w-3.5 h-10 rounded-full flex-shrink-0"
                  style={{ backgroundColor: mode.color }}
                  aria-hidden="true"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-aero-900 truncate">{mode.label}</p>
                    {isCurrent && (
                      <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-aero-100 text-aero-600 flex-shrink-0">
                        Active
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-aero-500">Time: {formatMinutes(minutes)}</span>
                    {requiredMins ? (
                      <span className={`flex items-center gap-1 text-[11px] font-medium ${meetsRequired ? 'text-green-600' : 'text-red-500'}`}>
                        {meetsRequired ? <Check className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                        min {formatMinutes(requiredMins)}
                      </span>
                    ) : null}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="px-5 py-3 border-t border-aero-100 bg-aero-50/50">
          <button onClick={onClose} className="w-full py-2.5 rounded-xl text-sm font-semibold text-aero-600 hover:bg-aero-100 transition-colors">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
